//#region Metric labels utils

/**
 * Please use the path from the `Observation` object, so we can just `get(...)` the value, rather
 * than having to do any additional logic when it comes time to resolve the value.
 */
export const obsMetricNames = {
  respirations: "respirations",
  o2Sats: "o2Sats",
  hypercapnicRespiratoryFailure: "hypercapnicRespiratoryFailure",
  o2Flow: "o2Flow",
  heartRate: "heartRate",
  bloodPressure: "bloodPressure",
  bloodPresSystolic: "bloodPresSystolic",
  bloodPresDiastolic: "bloodPresDiastolic",
  temperature: "temperature",
  consciousness: "consciousness",
  rhythm: "rhythm",
  bloodGlucose: "bloodGlucose",
  bloodAlcohol: "bloodAlcohol",
  painScore: "painScore",
  //# region neurovascular
  gcsTitle: "gcsTitle",
  gcsEyes: "neuro.eyeOpening",
  gcsVerbal: "neuro.verbal",
  gcsMovement: "neuro.motor",
  gcsTotal: "neuro.glasgowTotal",
  //#
  limbMovTitle: "limbMovTitle",
  limbMovRightArm: "neuro.movement.armRight",
  limbMovLeftArm: "neuro.movement.armLeft",
  limbMovRightLeg: "neuro.movement.legRight",
  limbMovLeftLeg: "neuro.movement.legLeft",
  //#
  pupilsTitle: "pupilsTitle",
  pupilLeftTitle: "pupilLeftTitle",
  pupilLeftSize: "neuro.pupil.leftSize",
  pupilLeftReaction: "neuro.pupil.leftReaction",

  pupilRightTitle: "pupilRightTitle",
  pupilRightSize: "neuro.pupil.rightSize",
  pupilRightReaction: "neuro.pupil.rightReaction",
  //#
  limbNeuroStatusTitle: "limbNeuroStatusTitle",

  limbNeuroRightArmTitle: "limbNeuroRightArmTitle",
  limbNeuroRightArmColour: "neuro.limbNeuro.rightArm.colour",
  limbNeuroRightArmWarmth: "neuro.limbNeuro.rightArm.warmth",
  limbNeuroRightArmSensation: "neuro.limbNeuro.rightArm.sensation",
  limbNeuroRightArmCapillaryRefill: "neuro.limbNeuro.rightArm.capillaryRefill",
  limbNeuroRightArmPulse: "neuro.limbNeuro.rightArm.pulse",
  limbNeuroRightArmMovement: "neuro.limbNeuro.rightArm.movement",

  limbNeuroLeftArmTitle: "limbNeuroLeftArmTitle",
  limbNeuroLeftArmColour: "neuro.limbNeuro.leftArm.colour",
  limbNeuroLeftArmWarmth: "neuro.limbNeuro.leftArm.warmth",
  limbNeuroLeftArmSensation: "neuro.limbNeuro.leftArm.sensation",
  limbNeuroLeftArmCapillaryRefill: "neuro.limbNeuro.leftArm.capillaryRefill",
  limbNeuroLeftArmPulse: "neuro.limbNeuro.leftArm.pulse",
  limbNeuroLeftArmMovement: "neuro.limbNeuro.leftArm.movement",

  limbNeuroRightLegTitle: "limbNeuroRightLegTitle",
  limbNeuroRightLegColour: "neuro.limbNeuro.rightLeg.colour",
  limbNeuroRightLegWarmth: "neuro.limbNeuro.rightLeg.warmth",
  limbNeuroRightLegSensation: "neuro.limbNeuro.rightLeg.sensation",
  limbNeuroRightLegCapillaryRefill: "neuro.limbNeuro.rightLeg.capillaryRefill",
  limbNeuroRightLegPulse: "neuro.limbNeuro.rightLeg.pulse",
  limbNeuroRightLegMovement: "neuro.limbNeuro.rightLeg.movement",

  limbNeuroLeftLegTitle: "limbNeuroLeftLegTitle",
  limbNeuroLeftLegColour: "neuro.limbNeuro.leftLeg.colour",
  limbNeuroLeftLegWarmth: "neuro.limbNeuro.leftLeg.warmth",
  limbNeuroLeftLegSensation: "neuro.limbNeuro.leftLeg.sensation",
  limbNeuroLeftLegCapillaryRefill: "neuro.limbNeuro.leftLeg.capillaryRefill",
  limbNeuroLeftLegPulse: "neuro.limbNeuro.leftLeg.pulse",
  limbNeuroLeftLegMovement: "neuro.limbNeuro.leftLeg.movement",

  //# endregion
  bedsideBloodPressureLeftArm: "bedsideBloodPressureLeftArm",
  bedsideBloodPressureRightArm: "bedsideBloodPressureRightArm",
  bedsideBloodPressureLaying: "bedsideBloodPressureLaying",
  bedsideBloodPressureStanding: "bedsideBloodPressureStanding",
  bedsideHeartRate: "bedsideHeartRate",
  ventilatorTidalVolume: "ventilatorTidalVolume",
  ventilatorMinuteVentilation: "ventilatorMinuteVentilation",
  ventilatorFrequencyRate: "ventilatorFrequencyRate",
  ventilatorPeakPressure: "ventilatorPeakPressure",
  ventilatorEndTidalCarbonDioxide: "ventilatorEndTidalCarbonDioxide",
  urinalysisAbnormalities: "urinalysisAbnormalities",
  urinalysisDistream: "urinalysisDistream",
  urinalysisPregnancy: "urinalysisPregnancy",
  urinalysisGlucose: "urinalysisGlucose",
  urinalysisProtein: "urinalysisProtein",
  urinalysisBlood: "urinalysisBlood",
  urinalysisBilirubin: "urinalysisBilirubin",
  urinalysisNitrite: "urinalysisNitrite",
  urinalysisUrobilinogen: "urinalysisUrobilinogen",
  urinalysisLeukocytes: "urinalysisLeukocytes",
  urinalysisPh: "urinalysisPh",
  urinalysisKetone: "urinalysisKetone",
  urinalysisSpecificGravity: "urinalysisSpecificGravity",
  /** riskScore === newsScore - NEWS2 is the scoring methodology - but everywhere we've called it a _riskScore_ */
  riskScore: "riskScore",
} as const;
/** @deprecated check the intention isn't to use `ObsMetricAlias` instead of `ObsMetricName` and replace if so please */
export type ObsMetricName = keyof typeof obsMetricNames;
export type ObsMetricAlias = typeof obsMetricNames[ObsMetricName];

// eslint-disable-next-line complexity
export const obsMetricToLabel = (metric: ObsMetricName | ObsMetricAlias): string => {

  switch (metric) {
    case obsMetricNames.respirations: return "Respirations";
    case obsMetricNames.o2Sats: return "O2 Saturation";
    case obsMetricNames.o2Flow: return "O2 Flow Rate";
    case obsMetricNames.hypercapnicRespiratoryFailure: return "Hypercapnic Respiratory Failure";
    case obsMetricNames.heartRate: return "Heart Rate";
    case obsMetricNames.bloodPressure:
    case obsMetricNames.bloodPresSystolic:
    case obsMetricNames.bloodPresDiastolic:
      return "Blood pressure";
    case obsMetricNames.temperature: return "Temperature";
    case obsMetricNames.rhythm: return "Rhythm";
    case obsMetricNames.bloodGlucose: return "Blood glucose level";
    case obsMetricNames.bloodAlcohol: return "Blood alcohol concentration";
    case obsMetricNames.painScore: return "Pain score";
    //# region neurovascular
    case obsMetricNames.gcsTitle: return "Glasgow Score";
    case obsMetricNames.gcsEyes: return "Eyes";
    case obsMetricNames.gcsVerbal: return "Verbal";
    case obsMetricNames.gcsMovement: return "Movement";
    case obsMetricNames.gcsTotal: return "Total";
    //#
    case obsMetricNames.pupilsTitle: return "Pupils";

    case obsMetricNames.pupilLeftTitle: return "Pupil left";
    case obsMetricNames.pupilLeftSize: return "Size";
    case obsMetricNames.pupilLeftReaction: return "Reaction";

    case obsMetricNames.pupilRightTitle: return "Pupil right";
    case obsMetricNames.pupilRightSize: return "Size";
    case obsMetricNames.pupilRightReaction: return "Reaction";
    //#
    case obsMetricNames.limbNeuroStatusTitle: return "Limb Neurovascular Status";

    case obsMetricNames.limbNeuroRightArmTitle: return "Right arm";
    case obsMetricNames.limbNeuroLeftArmTitle: return "Left arm";
    case obsMetricNames.limbNeuroLeftLegTitle: return "Left leg";
    case obsMetricNames.limbNeuroRightLegTitle: return "Right leg";

    case obsMetricNames.limbNeuroRightArmColour:
    case obsMetricNames.limbNeuroRightLegColour:
    case obsMetricNames.limbNeuroLeftLegColour:
    case obsMetricNames.limbNeuroLeftArmColour: return "Colour";

    case obsMetricNames.limbNeuroLeftArmWarmth:
    case obsMetricNames.limbNeuroLeftLegWarmth:
    case obsMetricNames.limbNeuroRightLegWarmth:
    case obsMetricNames.limbNeuroRightArmWarmth: return "Warmth";

    case obsMetricNames.limbNeuroLeftArmMovement:
    case obsMetricNames.limbNeuroRightLegMovement:
    case obsMetricNames.limbNeuroLeftLegMovement:
    case obsMetricNames.limbNeuroRightArmMovement: return "Movement";

    case obsMetricNames.limbNeuroLeftArmSensation:
    case obsMetricNames.limbNeuroLeftLegSensation:
    case obsMetricNames.limbNeuroRightLegSensation:
    case obsMetricNames.limbNeuroRightArmSensation: return "Sensation";

    case obsMetricNames.limbNeuroRightArmCapillaryRefill:
    case obsMetricNames.limbNeuroRightLegCapillaryRefill:
    case obsMetricNames.limbNeuroLeftLegCapillaryRefill:
    case obsMetricNames.limbNeuroLeftArmCapillaryRefill: return "Capillary refill";

    case obsMetricNames.limbNeuroLeftArmPulse:
    case obsMetricNames.limbNeuroRightLegPulse:
    case obsMetricNames.limbNeuroLeftLegPulse:
    case obsMetricNames.limbNeuroRightArmPulse: return "Pulse";
    //#
    case obsMetricNames.limbMovTitle: return "Limb Movement";
    case obsMetricNames.limbMovRightArm: return "Right arm";
    case obsMetricNames.limbMovLeftArm: return "Left arm";
    case obsMetricNames.limbMovRightLeg: return "Right leg";
    case obsMetricNames.limbMovLeftLeg: return "Left leg";
    //# endregion
    case obsMetricNames.bedsideBloodPressureLeftArm: return "Blood pressure (left arm)";
    case obsMetricNames.bedsideBloodPressureRightArm: return "Blood pressure (right arm)";
    case obsMetricNames.bedsideBloodPressureLaying: return "Blood pressure (laying)";
    case obsMetricNames.bedsideBloodPressureStanding: return "Blood pressure (standing)";
    case obsMetricNames.bedsideHeartRate: return "Heart Rate";
    case obsMetricNames.ventilatorTidalVolume: return "Tidal volume";
    case obsMetricNames.ventilatorMinuteVentilation: return "Minute ventilation";
    case obsMetricNames.ventilatorFrequencyRate: return "Frequency rate";
    case obsMetricNames.ventilatorPeakPressure: return "Peak pressure";
    case obsMetricNames.ventilatorEndTidalCarbonDioxide: return "End-tidal carbon dioxide";
    case obsMetricNames.urinalysisAbnormalities: return "Abnormalities";
    case obsMetricNames.urinalysisDistream: return "Midstream urine sent";
    case obsMetricNames.urinalysisPregnancy: return "Pregnancy";
    case obsMetricNames.urinalysisGlucose: return "Glucose";
    case obsMetricNames.urinalysisProtein: return "Protein";
    case obsMetricNames.urinalysisBlood: return "Blood";
    case obsMetricNames.urinalysisBilirubin: return "Bilirubin";
    case obsMetricNames.urinalysisNitrite: return "Nitrite";
    case obsMetricNames.urinalysisUrobilinogen: return "Urobilinogen";
    case obsMetricNames.urinalysisLeukocytes: return "Leukocytes";
    case obsMetricNames.urinalysisPh: return "pH";
    case obsMetricNames.urinalysisKetone: return "Ketone";
    case obsMetricNames.urinalysisSpecificGravity: return "Specific Gravity";
    case obsMetricNames.riskScore: return "NEWS2";
    default:
      if (!(metric || "").toLocaleLowerCase().includes("consciousness")) {
        console.warn(`Unknown metric: ${metric}. Default label returned.`);
      }
      return "Consciousness";
  }
};

const allMetrics = Object.keys(obsMetricNames) as ObsMetricName[];
const labelToMetric = allMetrics.reduce< Record< string, ObsMetricName > >(
  (a, metric) => {
    a[obsMetricToLabel(metric) ] = metric;

    return a;
  },
  {}
);


export const obsMetricLabelToObsMetric = (label: string): ObsMetricName => labelToMetric[label] || obsMetricNames.respirations;

export const abnormalitiesOptions = ["Negative", "Positive"];
export const leukocytesOptions = ["Negative", "Trace", "+", "++", "+++"];
export const nitriteOptions = ["Negative", "Positive"];
export const urobilinogenOptions = ["Normal", "+", "++", "+++"];
export const proteinOptions = ["Negative", "Trace", "+", "++", "+++"];
export const phOptions = ["5.0", "6.0", "6.5", "7.0", "7.5", "8.0", "8.5"];
export const bloodOptions = ["Negative", "Trace", "+", "++", "+++", "Frank blood"];
export const specificGravityOptions = ["1.000", "1.005", "1.010", "1.015", "1.020", "1.025", "1.030"];
export const ketoneOptions = ["Negative", "Trace", "+", "++", "+++"];
export const bilirubinOptions = ["Negative", "+", "++", "+++"];
export const glucoseOptions = ["Neg", "Trace", "+", "++", "+++"];
export const pregnancyOptions = ["Positive", "Negative"];
export const urinalysisOptions = {
  abnormalities : abnormalitiesOptions.map(option => ({ value: option, label: option })),
  leukocytes: leukocytesOptions.map(option => ({ value: option, label: option })),
  nitrite: nitriteOptions.map(option => ({ value: option, label: option })),
  urobilinogen: urobilinogenOptions.map(option => ({ value: option, label: option })),
  protein: proteinOptions.map(option => ({ value: option, label: option })),
  ph: phOptions.map(option => ({ value: option, label: option })),
  blood: bloodOptions.map(option => ({ value: option, label: option })),
  specificGravity: specificGravityOptions.map(option => ({ value: option, label: option })),
  ketone: ketoneOptions.map(option => ({ value: option, label: option })),
  bilirubin: bilirubinOptions.map(option => ({ value: option, label: option })),
  glucose: glucoseOptions.map(option => ({ value: option, label: option })),
  pregnancy: pregnancyOptions.map(option => ({ value: option, label: option })),
};
